// API for product operations using server endpoints
import { v4 as uuidv4 } from 'uuid';
import authService from '../utils/authService';

// API base URL - empty for relative paths in the same domain
const API_BASE = '';

// Interface for Product data from API
export interface ApiProduct {
  id: string;
  brand: string;
  type: string;
  colorway: string;
  size: string;
  sizeType: string;
  condition: string;
  price: number;
  ogBox: boolean;
  collab?: string; // Optional collaboration field
  description?: string; // Optional description field
  images: string[];
  createdAt: string;
  soldAt?: string; // Optional field for when the product was sold
  status: 'staged' | 'active' | 'sold';
}

// Interface for front-end product display
export interface FrontendProduct {
  id: string;
  name: string;
  brand: string;
  price: number;
  image: string;
  images?: string[]; // Add the images array
  sizes: string[];
  sizeType: string;
  colorway: string;
  condition: string;
  collab?: string; // Optional collaboration field
  description?: string; // Optional description field
  size?: string;   // Single size value
  sizeDisplay?: string; // Formatted size display
}

// Interface for failed uploads
export interface FailedUpload {
  file: string;
  error: string;
}

// Helper function to get authorization headers (fallback for non-JSON requests)
const getAuthHeaders = (): Record<string, string> => {
  const accessToken = localStorage.getItem('accessToken');
  return accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {};
};

// Helper function for authenticated fetch (fallback for file uploads)
const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
  const authHeaders = getAuthHeaders();
  const headers = {
    ...options.headers,
    ...authHeaders
  };
  
  return fetch(url, {
    ...options,
    headers
  });
};

/**
 * Upload multiple products at once (bulk upload)
 */
export const uploadProducts = async (productData: Omit<ApiProduct, 'id' | 'createdAt' | 'status'>[]): Promise<ApiProduct[]> => {
  try {
    console.log(`Attempting to upload ${productData.length} products...`);
    
    const responseData = await authService.apiRequest(
      `${API_BASE}/api/admin/products`,
      'POST',
      productData
    );
    
    const newProducts = responseData.data || [];
    console.log(`Successfully uploaded ${newProducts.length} products to the server`);
    return newProducts;
  } catch (error) {
    console.error('Error uploading products:', error);
    throw error;
  }
};

/**
 * Get all products (Admin endpoint to ensure all data is included)
 */
export const getAllProducts = async (): Promise<ApiProduct[]> => {
  try {
    const data = await authService.apiRequest(`${API_BASE}/api/admin/products`, 'GET');
    // Handle both response formats: direct array or {success: true, data: []}
    if (Array.isArray(data)) {
      return data;
    } else if (data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching all products:', error);
    throw error;
  }
};

/**
 * Get only active products for the front-end display
 */
export const getActiveProducts = async (): Promise<FrontendProduct[]> => {
  try {
    const response = await fetch(`${API_BASE}/api/products?active=true`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    // Handle both formats: array directly or {success: true, data: []}
    const frontendProducts = Array.isArray(data) ? data : (data.data || []);
    return frontendProducts;
  } catch (error) {
    console.error('Error fetching active products:', error);
    throw error;
  }
};

/**
 * Update product status from staged to active
 */
export const publishProducts = async (productIds: string[]): Promise<ApiProduct[]> => {
  try {
    console.log(`Attempting to publish ${productIds.length} products...`);
    
    const responseData = await authService.apiRequest(
      `${API_BASE}/api/admin/publish`,
      'POST',
      { productIds }
    );
    
    if (!responseData.success) {
      throw new Error(responseData.message || 'Failed to publish products');
    }
    
    return responseData.data || [];
  } catch (error) {
    console.error('Error publishing products:', error);
    throw error;
  }
};

/**
 * Upload product images to the server
 */
export const uploadProductImages = async (productId: string, files: File[], position?: number): Promise<{
  urls: string[]; 
  failedUploads?: Array<{filename: string; reason: string}>
}> => {
  try {
    // Create a FormData object for file upload
    const formData = new FormData();
    formData.append('productId', productId);
    
    if (position !== undefined) {
      formData.append('position', position.toString());
    }
    
    // Add all files to FormData with the key 'files' that the backend expects
    files.forEach((file) => {
      formData.append('files', file);
    });
    
    // Log the API request for debugging
    console.log(`Attempting to upload images to ${API_BASE}/api/admin/upload-image`);
    
    // For file uploads, we need to use authenticatedFetch since authService.apiRequest expects JSON
    // But let's ensure the auth headers are correct
    const accessToken = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    const response = await fetch(`${API_BASE}/api/admin/upload-image`, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to upload images');
    }
    
    return {
      urls: data.urls || [],
      failedUploads: data.failedUploads
    };
  } catch (error) {
    console.error('Error uploading images:', error);
    
    // Log more detailed error information
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    
    throw error;
  }
};

/**
 * Clear all products (for testing/admin purposes)
 */
export const clearAllProducts = async (): Promise<void> => {
  try {
    await authService.apiRequest(`${API_BASE}/api/admin/clear-products`, 'POST');
  } catch (error) {
    console.error('Error clearing products:', error);
    throw error;
  }
};

/**
 * Get a product by ID
 */
export const getProductById = async (productId: string): Promise<ApiProduct | null> => {
  try {
    const data = await authService.apiRequest(`${API_BASE}/api/products/${productId}`, 'GET');
    return data;
  } catch (error) {
    console.error(`Error fetching product ${productId}:`, error);
    throw error;
  }
};

/**
 * Get public product details by ID
 * This uses the public API endpoint that doesn't require authentication
 */
export const getPublicProductDetail = async (productId: string): Promise<any> => {
  try {
    // Only use the public products endpoint
    const response = await fetch(`${API_BASE}/api/products/${productId}`);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching public product details for ${productId}:`, error);
    throw error;
  }
};

/**
 * Delete a product
 */
export const deleteProduct = async (productId: string): Promise<boolean> => {
  try {
    console.log(`Attempting to delete product: ${productId}`);
    
    const data = await authService.apiRequest(
      `${API_BASE}/api/admin/products/${productId}`,
      'DELETE'
    );
    
    console.log('Delete response:', data);
    
    if (!data.success) {
      console.error('API returned success: false', data);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error(`Error deleting product ${productId}:`, error);
    throw error;
  }
};

/**
 * Update a product
 */
export const updateProduct = async (productId: string, productData: Partial<Omit<ApiProduct, 'id' | 'createdAt'>>): Promise<ApiProduct | null> => {
  try {
    const data = await authService.apiRequest(
      `${API_BASE}/api/admin/products/${productId}`,
      'PUT',
      productData
    );
    
    return data.success ? data.data : null;
  } catch (error) {
    console.error(`Error updating product ${productId}:`, error);
    throw error;
  }
};

/**
 * Delete an image from a product by position
 */
export const deleteProductImage = async (productId: string, imagePosition: number): Promise<ApiProduct | null> => {
  try {
    // Use the admin products endpoint with a special request to delete image by position
    const data = await authService.apiRequest(
      `/api/admin/products/${productId}`,
      'PATCH',
      { deleteImageAtPosition: imagePosition }
    );
    
    return data.success ? data.data : null;
  } catch (error) {
    console.error(`Error deleting image from product ${productId}:`, error);
    throw error;
  }
};

/**
 * Delete multiple products at once
 */
export const deleteMultipleProducts = async (productIds: string[]): Promise<boolean> => {
  try {
    console.log(`Attempting to delete ${productIds.length} products...`);
    
    // Since we don't have a bulk delete endpoint, we'll delete them one by one
    const deletePromises = productIds.map(id => 
      authService.apiRequest(`${API_BASE}/api/admin/products/${id}`, 'DELETE')
    );
    
    await Promise.all(deletePromises);
    
    return true;
  } catch (error) {
    console.error('Error deleting multiple products:', error);
    throw error;
  }
}; 